import {
  isSkillAllowedRelativePath,
  normalizeSkillRelativePath,
  SKILL_ENTRYPOINT,
  SKILL_MAX_FILE_BYTES,
  SKILL_MAX_FILES,
  SKILL_MAX_TREE_BYTES,
} from "encamp-shared/src/s3/skills/paths";

function utf8ByteLength(content: string): number {
  return new TextEncoder().encode(content).byteLength;
}

/**
 * Validate a draft file the same way the skill S3 writer does, before a skill id exists.
 * Returns the normalized relative path.
 */
export function prepareDraftSkillFile(params: {
  path: string;
  content: string;
  files: Record<string, string>;
  rejectExisting?: boolean;
}): string {
  const normalized = normalizeSkillRelativePath(params.path);
  if (!normalized || !isSkillAllowedRelativePath(normalized)) {
    throw new Error(`Skill file type not allowed: ${params.path}`);
  }
  const exists = Object.hasOwn(params.files, normalized);
  if (params.rejectExisting && exists) {
    throw new Error("A file already exists at that path.");
  }

  const bytes = utf8ByteLength(params.content);
  if (bytes > SKILL_MAX_FILE_BYTES) {
    throw new Error(`Skill file exceeds max size (${SKILL_MAX_FILE_BYTES} bytes)`);
  }
  if (!exists && Object.keys(params.files).length >= SKILL_MAX_FILES) {
    throw new Error(`Skill tree exceeds max file count (${SKILL_MAX_FILES})`);
  }

  let otherBytes = 0;
  for (const [path, content] of Object.entries(params.files)) {
    if (path === normalized) {
      continue;
    }
    otherBytes += utf8ByteLength(content);
  }
  if (otherBytes + bytes > SKILL_MAX_TREE_BYTES) {
    throw new Error(`Skill tree exceeds max size (${SKILL_MAX_TREE_BYTES} bytes)`);
  }

  return normalized;
}

/** Include the open editor buffer so dialog save uploads unsaved text. */
export function mergedDraftFiles(
  files: Record<string, string>,
  selectedPath: string | null,
  draft: string
): Record<string, string> {
  if (!selectedPath) {
    return files;
  }
  return { ...files, [selectedPath]: draft };
}

/** Files written after create. SKILL.md is stored as initialSkillMd. */
export function extraDraftFiles(files: Record<string, string>): [string, string][] {
  return Object.entries(files).filter(([path]) => path !== SKILL_ENTRYPOINT);
}

export function skillMdForCreate(files: Record<string, string>): string | undefined {
  const skillMd = files[SKILL_ENTRYPOINT]?.trim();
  return skillMd ? skillMd : undefined;
}
